/**
 * Semantic lint rules for a parsed SpecAst. Pure (no I/O) — runs offline.
 *
 * @module spec/lint
 */

import { validateMacroReferences, validateWorkflowInputExpressions } from '@chatsdet/types';
import type { JourneyNode, SpecAst, SpecIssue } from './grammar.js';

export function lintSpec(ast: SpecAst): SpecIssue[] {
  const issues: SpecIssue[] = [];

  // R1 — at least one run-failing (fail or fatal) assertion somewhere in the journey.
  const isRunFailing = (severity: string) => severity === 'fail' || severity === 'fatal';
  const hasFailAssertion = anyNode(ast.journey, (n) => {
    if (n.kind === 'action') return n.assertions.some((a) => isRunFailing(a.severity));
    if (n.kind === 'assertion') return isRunFailing(n.severity);
    if (n.kind === 'audioAssertion') return isRunFailing(n.severity);
    return false;
  });
  if (!hasFailAssertion) {
    issues.push({
      severity: 'warn',
      message:
        'Spec has no fail- or fatal-severity assertions — the case will pass even if the feature is broken.',
      hint: 'Add `- assert (fail): …` (or `(fatal)` for must-stop preconditions) under at least one action.',
    });
  }

  // R2 — requirementRef present.
  if (!ast.frontmatter.requirementRef) {
    issues.push({
      severity: 'warn',
      message: '`requirementRef` is missing from frontmatter — untraceable specs rot.',
    });
  }

  // R3 — non-empty Spirit/Preflight/Cleanup if present (parser drops empties to undefined,
  // so this is mostly a no-op; left for future strictness).

  // R4 — input expressions are canonical and reference only preceding values.
  const declaredInputs = new Set(ast.inputs.map((i) => i.name));
  for (const issue of validateWorkflowInputExpressions(ast.inputs)) {
    issues.push({
      severity: 'error',
      message: `${issue.message} (input "${issue.inputName}" default)`,
      hint: issue.hint,
    });
  }

  for (const scenario of ast.scenarios) {
    const overriddenNames = new Set(Object.keys(scenario.values));
    for (const issue of validateWorkflowInputExpressions(ast.inputs, scenario.values)) {
      if (!overriddenNames.has(issue.inputName)) continue;
      issues.push({
        severity: 'error',
        message: `${issue.message} (scenario "${scenario.name}")`,
        hint: issue.hint,
      });
    }
  }

  // Names resolvable as `$var.<name>` at the start of the journey. Action
  // field names become variables only *after* that action has completed, so
  // they must not make a self-reference in their own value look valid.
  const promotedVars = new Set<string>();
  declaredInputs.forEach((input) => promotedVars.add(input));

  const inspectString = (s: string, ctx: string) => {
    for (const issue of validateMacroReferences(s, { allowedBareTokens: ['CREDENTIAL'] })) {
      issues.push({
        severity: 'error',
        message: `${issue.message} (${ctx})`,
        hint: issue.suggestion ? `Use the canonical form \`${issue.suggestion}\`.` : undefined,
      });
    }

    for (const ref of extractMacroRefs(s)) {
      // Uppercase references are macro candidates and were validated above.
      // `$CREDENTIAL` is a credential-input sentinel, not a runtime macro.
      if (/^[A-Z][A-Z0-9_]*$/.test(ref) || ref === 'CREDENTIAL') continue;
      if (ref.startsWith('var.')) {
        const name = ref.slice('var.'.length);
        if (!promotedVars.has(name)) {
          issues.push({
            severity: 'error',
            message: `\`$var.${name}\` referenced in ${ctx} but not declared in \`## Inputs\`, via \`- capture:\`, or as a field.`,
            hint:
              'If this is authored data, add `- capture: ' +
              name +
              '` to the action that produces it.',
          });
        }
      } else if (!declaredInputs.has(ref)) {
        issues.push({
          severity: 'error',
          message: `\`$${ref}\` referenced in ${ctx} but not declared in \`## Inputs\`.`,
        });
      }
    }
  };

  // Visit in execution order. A capture or field emitted by an action is
  // available to later nodes, but never to the action that declares it.
  const lintJourney = (nodes: JourneyNode[]) => {
    for (const node of nodes) {
      const ctx = nodeCtx(node);
      if (node.description) inspectString(node.description, ctx);
      if (node.kind === 'navigate') inspectString(node.pageUrl, ctx);
      if (node.kind === 'action') {
        inspectString(node.title, ctx);
        node.fields.forEach((field) => inspectString(field.value, ctx));
        node.assertions.forEach((assertion) => inspectString(assertion.condition, ctx));
        node.capture.forEach((capture) => promotedVars.add(capture));
        node.fields.forEach((field) => promotedVars.add(field.name));
      }
      if (node.kind === 'assertion') inspectString(node.condition, ctx);
      if (node.kind === 'audioAssertion' && node.prompt) inspectString(node.prompt, ctx);
      if (node.kind === 'notification') {
        inspectString(node.to, ctx);
        inspectString(node.body, ctx);
        if (node.subject) inspectString(node.subject, ctx);
      }
      if (node.kind === 'condition') {
        inspectString(node.expression, ctx);
        lintJourney(node.thenBranch);
        lintJourney(node.elseBranch);
      }
    }
  };
  lintJourney(ast.journey);

  // R5 — at most one default scenario.
  const defaults = ast.scenarios.filter((s) => s.isDefault);
  if (defaults.length > 1) {
    issues.push({
      severity: 'error',
      message: `Multiple scenarios marked default: ${defaults.map((s) => s.name).join(', ')}.`,
    });
  }

  // R6 — skipped scenarios cite a reason.
  for (const s of ast.scenarios) {
    if (!s.includeInSuite && !s.skipReason) {
      issues.push({
        severity: 'warn',
        message: `Scenario "${s.name}" is skipped without a reason. Use *(skip: <ref>)*.`,
      });
    }
  }

  // R7 — scenario values reference declared inputs.
  for (const s of ast.scenarios) {
    for (const key of Object.keys(s.values)) {
      if (!declaredInputs.has(key)) {
        issues.push({
          severity: 'warn',
          message: `Scenario "${s.name}" overrides input "${key}" which is not declared in \`## Inputs\`.`,
        });
      }
    }
  }

  // R8 — attachments referenced in the journey/notifications must be declared.
  const declaredAttachments = new Set(ast.attachments.map((a) => a.ref));
  walkNodes(ast.journey, (n) => {
    if (n.kind === 'audio' && !declaredAttachments.has(n.attachmentRef)) {
      issues.push({
        severity: 'error',
        message: `Play audio references unknown attachment "${n.attachmentRef}".`,
      });
    }
    if (n.kind === 'audioAssertion' && n.reference && !declaredAttachments.has(n.reference)) {
      issues.push({
        severity: 'error',
        message: `Audio assertion references unknown attachment "${n.reference}".`,
      });
    }
    if (n.kind === 'notification' && n.attach && !declaredAttachments.has(n.attach)) {
      issues.push({
        severity: 'error',
        message: `Notification attaches unknown attachment "${n.attach}".`,
      });
    }
  });

  // R9 — click-by-click language (warn).
  walkNodes(ast.journey, (n) => {
    if (n.kind !== 'action') return;
    const text = (n.description ?? '') + ' ' + n.title;
    if (/\bclick\s+(?:the\s+)?["']?\w+/i.test(text) && /,\s*then\s+click/i.test(text)) {
      issues.push({
        severity: 'warn',
        message: `Action "${n.title}" reads as click-by-click UI mechanics — prefer human intent.`,
      });
    }
  });

  return issues;
}

/* ── helpers ─────────────────────────────────────────────────────────── */

function anyNode(nodes: JourneyNode[], pred: (n: JourneyNode) => boolean): boolean {
  for (const n of nodes) {
    if (pred(n)) return true;
    if (n.kind === 'condition') {
      if (anyNode(n.thenBranch, pred) || anyNode(n.elseBranch, pred)) return true;
    }
  }
  return false;
}

function walkNodes(nodes: JourneyNode[], visit: (n: JourneyNode) => void): void {
  for (const n of nodes) {
    visit(n);
    if (n.kind === 'condition') {
      walkNodes(n.thenBranch, visit);
      walkNodes(n.elseBranch, visit);
    }
  }
}

function nodeCtx(n: JourneyNode): string {
  if (n.kind === 'action') return `action "${n.title}"`;
  if (n.kind === 'assertion') return `assertion`;
  if (n.kind === 'navigate') return `navigate`;
  if (n.kind === 'condition') return `condition`;
  if (n.kind === 'notification') return `notification`;
  if (n.kind === 'seed') return `seed "${n.specName}"`;
  if (n.kind === 'setup') return `setup "${n.specName}"`;
  return n.kind;
}

/**
 * Pull every `$<token>` reference out of a string. Recognizes:
 *   $foo, $foo.bar, $foo_bar, $RANDOM_STRING(8), $var.captured
 * Excludes false positives like prices ($5).
 */
function extractMacroRefs(s: string): string[] {
  const out: string[] = [];
  const re = /(?<!\\)\$([A-Za-z_][A-Za-z0-9_.]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    // Drop trailing dots — they're sentence punctuation after a token like
    // `$var.foo.`, not part of the variable name.
    out.push(m[1].replace(/\.+$/, ''));
  }
  return out;
}
